import { motion } from "framer-motion";
import { Calendar, Clock, MapPin } from "@phosphor-icons/react";

const formatEventDate = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "long", year: "numeric" });

const imgSrc = (url, apiUrl) => (url.startsWith("http") ? url : `${apiUrl}${url}`);

const IMG_COLS = { 1: "grid-cols-1", 2: "grid-cols-2", 3: "grid-cols-3" };

export const EventCard = ({ ev, index, apiUrl }) => {
  const images = ev.images || [];
  const hasImages = images.length > 0;
  const bookingUrl = ev.booking_url || `mailto:info@js-blondsalon.de?subject=${encodeURIComponent(`Anmeldung ${ev.title}`)}`;
  const isExternal = bookingUrl.startsWith("http");
  const slug = ev.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  const spotsLeft = typeof ev.spots_left === "number" ? ev.spots_left : null;
  const soldOut = spotsLeft === 0;
  const badge = ev.highlight || (spotsLeft !== null && spotsLeft > 0 && spotsLeft <= 5 ? "Letzte Plätze" : null);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="relative bg-[#1a1a1a] border border-white/10 overflow-hidden"
      data-testid={`event-${slug}`}
    >
      {(badge || soldOut || ev.is_free) && (
        <span
          data-testid={`event-${slug}-badge`}
          className={`absolute top-0 right-0 z-10 px-4 py-2 text-[10px] uppercase tracking-[0.25em] font-bold ${
            soldOut ? "bg-white/10 text-white/60" : ev.is_free && !badge ? "bg-white text-[#0a0a0a]" : "bg-[#D4AF37] text-[#0a0a0a]"
          }`}
        >
          {soldOut ? "Ausgebucht" : badge || "Kostenlos"}
        </span>
      )}
      <div className={hasImages ? "grid grid-cols-1 lg:grid-cols-5" : ""}>
        {hasImages && (
          <div className={`lg:col-span-2 grid ${IMG_COLS[images.length]} gap-px`}>
            {images.map((img, i) => (
              <img key={img} src={imgSrc(img, apiUrl)} alt={`${ev.title} ${i + 1}`} loading="lazy"
                className="w-full h-48 lg:h-full object-cover object-top" />
            ))}
          </div>
        )}
        <div className={`${hasImages ? "lg:col-span-3" : ""} p-6 sm:p-8 flex flex-col ${hasImages ? "justify-between" : "sm:flex-row sm:items-center justify-between gap-6"}`}>
          <div>
            <span className="text-[#D4AF37] text-xs uppercase tracking-[0.2em]">{ev.category}</span>
            <h3 className="text-2xl sm:text-3xl font-bold text-white mt-2 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
              {ev.title}
            </h3>
            {ev.subtitle && <p className="text-white/50 text-sm mb-4">{ev.subtitle}</p>}
            {ev.description && (
              <p className="text-white/70 text-sm leading-relaxed mb-6 max-w-xl whitespace-pre-line">{ev.description}</p>
            )}
            <div className="flex flex-wrap gap-4 text-xs text-white/50 mb-4">
              {ev.date && <span className="flex items-center gap-1.5"><Calendar size={14} className="text-[#D4AF37]" weight="fill" /> {formatEventDate(ev.date)}</span>}
              {ev.time && <span className="flex items-center gap-1.5"><Clock size={14} className="text-[#D4AF37]" weight="fill" /> {ev.time}</span>}
              {ev.location && <span className="flex items-center gap-1.5"><MapPin size={14} className="text-[#D4AF37]" weight="fill" /> {ev.location}</span>}
            </div>
            {ev.price_info && <p className="text-white/40 text-xs">{ev.price_info}</p>}
            {spotsLeft !== null && (
              <div className="mt-4" data-testid={`event-${slug}-spots`}>
                <div className="flex items-center gap-3">
                  <div className="h-1 w-32 bg-white/10 overflow-hidden">
                    <div
                      className="h-full bg-[#D4AF37]"
                      style={{ width: `${ev.spots_total ? Math.min(100, Math.round(((ev.spots_total - spotsLeft) / ev.spots_total) * 100)) : 80}%` }}
                    />
                  </div>
                  <span className={`text-xs font-bold tracking-wide ${soldOut ? "text-white/50" : "text-[#D4AF37]"}`}>
                    {soldOut ? "Ausgebucht" : `Nur noch ${spotsLeft} ${spotsLeft === 1 ? "Platz" : "Plätze"} frei`}
                  </span>
                </div>
              </div>
            )}
          </div>
          {soldOut ? (
            <a
              href={`mailto:info@js-blondsalon.de?subject=${encodeURIComponent(`Warteliste ${ev.title}`)}`}
              data-testid={`event-${slug}-btn`}
              className={`${hasImages ? "mt-6 inline-block" : "flex-shrink-0"} text-center px-8 py-3 border border-white/30 text-white text-xs uppercase tracking-[0.2em] font-bold hover:bg-white/10 transition-colors duration-300`}
            >
              Auf die Warteliste
            </a>
          ) : (
          <a
            href={bookingUrl}
            target={isExternal ? "_blank" : undefined}
            rel={isExternal ? "noopener noreferrer" : undefined}
            data-testid={`event-${slug}-btn`}
            className={`${hasImages ? "mt-6 inline-block" : "flex-shrink-0"} text-center px-8 py-3 bg-[#D4AF37] text-[#0a0a0a] text-xs uppercase tracking-[0.2em] font-bold hover:bg-white transition-colors duration-300`}
          >
            {ev.button_text || "Jetzt anfragen"}
          </a>
          )}
        </div>
      </div>
    </motion.div>
  );
};
